import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

type Pack = {
  id: string;
  credits: number;
  label: string;
  suggestedUsd: number;
  purchasable?: boolean;
  stripeConfigured?: boolean;
};

export const PricingPage: React.FC = () => {
  const { token, refreshBalance, user, productConfig, balance } = useAuth();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const billingEnabled = Boolean(productConfig?.billingEnabled);
  const isAlphaFree =
    productConfig?.restorationsCurrentlyFree ||
    balance?.restorationsCurrentlyFree ||
    productConfig?.creditMode === 'disabled' ||
    productConfig?.creditMode === 'metered' ||
    productConfig?.stage === 'alpha' ||
    !productConfig;

  useEffect(() => {
    if (!billingEnabled) return;
    api
      .packs()
      .then((r) => {
        setPacks(r.packs || []);
        setLoadError(null);
      })
      .catch(() => {
        setPacks([]);
        setLoadError('Pricing is temporarily unavailable. Please try again later.');
      });
  }, [billingEnabled]);

  const buy = async (packId: string) => {
    if (!user) {
      window.location.href = '/login?next=/pricing';
      return;
    }
    setBusy(packId);
    setMessage(null);
    try {
      const session = await api.checkout(packId, token);
      await refreshBalance();
      if (session.mock) {
        setMessage('Test checkout completed in this environment.');
      } else if (session.url) {
        window.location.href = session.url;
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Checkout failed');
    } finally {
      setBusy(null);
    }
  };

  if (!billingEnabled) {
    return (
      <main className="kin-page">
        <h1>Pricing</h1>
        <p className="kin-section-lede">
          {isAlphaFree
            ? 'Kinvera is currently free during Alpha. Restorations do not require credits while we validate quality and operating costs.'
            : 'Billing is not active yet. Credit packs will appear here when purchases are enabled.'}
        </p>
        <p className="kin-banner">
          No purchase is required right now. Sign in and restore family photographs
          {isAlphaFree ? ' at no charge' : ''}.
        </p>
        <div className="kin-cta-row" style={{ marginTop: '1.5rem' }}>
          <Link to="/login?next=%2Fapp" className="kin-btn kin-btn-primary">
            {isAlphaFree ? 'Try Kinvera during Alpha' : 'Open Studio'}
          </Link>
          <Link to="/how-it-works" className="kin-btn kin-btn-ghost">
            How it works
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="kin-page">
      <h1>Pricing</h1>
      <p className="kin-section-lede">
        Pay per restoration — not per month. <strong>1 credit</strong> = analysis + restore +{' '}
        <strong>1 included tweak</strong>. Extra tweaks use half a credit.
      </p>
      {message && <p className="kin-banner">{message}</p>}
      {loadError && <p className="kin-banner kin-banner-error">{loadError}</p>}
      <div className="kin-pricing-grid">
        {packs.map((pack) => (
          <article key={pack.id} className="kin-price-card">
            <h2>{pack.label}</h2>
            {pack.stripeConfigured ? (
              <p className="kin-price">
                ${Number(pack.suggestedUsd).toFixed(2)}
                <span> · {pack.credits} restorations</span>
              </p>
            ) : (
              <p className="kin-price">
                {pack.credits}
                <span> restorations</span>
              </p>
            )}
            {pack.stripeConfigured && (
              <p>~${(Number(pack.suggestedUsd) / pack.credits).toFixed(2)} per restoration</p>
            )}
            <button
              type="button"
              className="kin-btn kin-btn-primary"
              disabled={busy === pack.id || pack.purchasable === false}
              onClick={() => buy(pack.id)}
            >
              {busy === pack.id ? 'Working…' : 'Get credits'}
            </button>
          </article>
        ))}
      </div>
      {!loadError && packs.length === 0 && (
        <p className="kin-footnote">Loading credit packs…</p>
      )}
      <p className="kin-footnote">
        Manage purchases anytime from <Link to="/app/account">Account</Link>. See{' '}
        <Link to="/privacy">privacy</Link> for how photos are handled.
      </p>
    </main>
  );
};
